/**
 * The web SDK for client packages: `@databink/web/extension`, the only part of the web app a
 * client may import (the lint boundary rule enforces it). A client's `web/index.tsx`
 * default-exports `defineWebExtension({ client, slots })`, and building the web app with
 * DATABINK_CLIENT=<slug> compiles it in. Nothing a client writes edits a core screen: it
 * fills slots the core publishes in its registry (spec 10.6), and the components here are
 * the same ones the core screens use, so client screens look and behave like the rest.
 */
import type { BrandMarks, SlotMap, WebExtension } from './extension-types.js';

export type {
  ActionContribution,
  BrandMarks,
  DashboardContribution,
  HelpContribution,
  ListActionContribution,
  ListFilter,
  ListSlotProps,
  NavContribution,
  PanelContribution,
  RecordSlotProps,
  RouteContribution,
  ScreenRecord,
  SlotContribution,
  SlotKey,
  SlotMap,
  TabContribution,
  WebExtension,
} from './extension-types.js';
export type { AppEntity, AppField, AppMeta } from './lib/meta.js';
export type { ListQuery } from './components/record-list.js';

/**
 * Declare a client's screen extensions. Slot ids and what each receives are typed from the
 * core's published slots, so a slot a core upgrade removes fails this package's build.
 * `brand` puts the client's own icon and logo where the app shows its product name.
 */
export function defineWebExtension<const S extends SlotMap>(extension: {
  client: string;
  slots: S;
  brand?: BrandMarks;
}): WebExtension {
  return extension;
}

// Data: the same queries and permissions as the core screens.
export {
  useRecords,
  useReport,
  CompactRecordList,
  RecordList,
  NewRecordButton,
} from './components/record-list.js';
export { useAppMeta, useEntity, findEntity, fieldOf, optionsFor } from './lib/meta.js';
export { trpc, trpcClient, errorMessage } from './lib/trpc.js';

// Showing records and values.
export { RecordLink, FieldValue, StateBadge, Money } from './components/fields.js';
export { RecordTable } from './components/record-table.js';
export { RecordForm } from './components/record-form.js';
export {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  countryName,
  recordTitle,
} from './lib/format.js';

// The UI kit.
export {
  Badge,
  Button,
  buttonClass,
  Card,
  cn,
  EmptyState,
  ErrorBanner,
  Input,
  Label,
  Modal,
  NativeSelect,
  PageHeader,
  Spinner,
  Table,
  TD,
  Textarea,
  TH,
} from './components/ui.js';

// Help pages (`app.help`), in the same look as the core's guides.
export { GuideSection, GuideSteps, GuideTip, OnScreen } from './help/guide.js';
